import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'
import {
    Button, LinearProgress, MenuItem, Select, Table, TableBody, TableCell, TableContainer,
    TableHead, TablePagination, TableRow, TextField,
} from '@mui/material'
import { H3 } from '../../../layouts/TextStyles'
import { tableStyles, ui } from '../../../utils/uiTokens'
import { schoolApiSlice, School } from '../../../features/school/schoolSlice'
import { Content, Header, Toolbar, Wrapper } from '../../admin/mentorSalaryPage/styles'
import SchoolAdminsDialog from './SchoolAdminsDialog'
import { titleOf } from '../titleOf'

const PAGE_SIZE = 20

// Модератор: справочник школ -> админы школы и её ученики
const SchoolsPage: React.FC = () => {
    const { formatMessage, locale } = useIntl()
    const navigate = useNavigate()
    const [regionId, setRegionId] = useState<number>(0)
    const [districtId, setDistrictId] = useState<number>(0)
    const [search, setSearch] = useState('')
    const [query, setQuery] = useState('')
    const [page, setPage] = useState(0)
    const [adminsFor, setAdminsFor] = useState<School | null>(null)

    const { data: regions = [] } = schoolApiSlice.useGetRegionsQuery()
    const { data: districts = [] } = schoolApiSlice.useGetChildLocationsQuery(regionId, { skip: !regionId })
    const { data, isFetching } = schoolApiSlice.useSearchSchoolsQuery({
        regionId, districtId, query, page, size: PAGE_SIZE,
    })

    // Поиск по мере ввода, без запроса на каждую букву
    useEffect(() => {
        const timer = setTimeout(() => {
            setQuery(search.trim())
            setPage(0)
        }, 300)
        return () => clearTimeout(timer)
    }, [search])

    // Области загружены всегда, районы - только для выбранной области
    const locationTitle = (school: School) => {
        const region = regions.find((r) => r.id === school.regionId)
        const district = districts.find((d) => d.id === school.districtId)
        return [region && titleOf(region, locale), district && titleOf(district, locale)].filter(Boolean).join(' · ') || '-'
    }

    return (
        <Wrapper>
            <Content>
                <Header>
                    <H3>{formatMessage({ id: 'schools' })}</H3>
                </Header>

                <Toolbar>
                    <Select
                        size="small"
                        displayEmpty
                        value={regionId}
                        onChange={(e) => {
                            setRegionId(Number(e.target.value))
                            setDistrictId(0)
                            setPage(0)
                        }}
                        sx={selectSx}
                    >
                        <MenuItem value={0}>{formatMessage({ id: 'schoolAllRegions' })}</MenuItem>
                        {regions.map((r) => (
                            <MenuItem key={r.id} value={r.id}>{titleOf(r, locale)}</MenuItem>
                        ))}
                    </Select>

                    {!!regionId && districts.length > 0 && (
                        <Select
                            size="small"
                            displayEmpty
                            value={districtId}
                            onChange={(e) => {
                                setDistrictId(Number(e.target.value))
                                setPage(0)
                            }}
                            sx={selectSx}
                        >
                            <MenuItem value={0}>{formatMessage({ id: 'schoolAllDistricts' })}</MenuItem>
                            {districts.map((d) => (
                                <MenuItem key={d.id} value={d.id}>{titleOf(d, locale)}</MenuItem>
                            ))}
                        </Select>
                    )}

                    <TextField
                        size="small"
                        placeholder={formatMessage({ id: 'schoolSearchPlaceholder' })}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        sx={{ minWidth: 280 }}
                    />
                </Toolbar>

                {isFetching && <LinearProgress />}
                <TableContainer sx={tableStyles.container}>
                    <Table>
                        <TableHead sx={tableStyles.head}>
                            <TableRow>
                                <TableCell>{formatMessage({ id: 'schoolTitle' })}</TableCell>
                                <TableCell>{formatMessage({ id: 'schoolLocation' })}</TableCell>
                                <TableCell align="right" />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(data?.data ?? []).map((school) => (
                                <TableRow key={school.id} sx={tableStyles.row}>
                                    <TableCell sx={{ fontWeight: 600 }}>{titleOf(school, locale)}</TableCell>
                                    <TableCell sx={{ color: ui.sub }}>{locationTitle(school)}</TableCell>
                                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                        <Button size="small" onClick={() => setAdminsFor(school)}>
                                            {formatMessage({ id: 'schoolAdmins' })}
                                        </Button>
                                        <Button size="small" onClick={() => navigate(`/schools/${school.id}/students`)}>
                                            {formatMessage({ id: 'schoolStudents' })}
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {!isFetching && (data?.data ?? []).length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={3} align="center" sx={{ color: ui.muted, py: 4 }}>
                                        {formatMessage({ id: 'noData' })}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={data?.totalCount ?? 0}
                    page={page}
                    rowsPerPage={PAGE_SIZE}
                    rowsPerPageOptions={[PAGE_SIZE]}
                    onPageChange={(e, p) => setPage(p)}
                />
            </Content>

            {adminsFor && <SchoolAdminsDialog school={adminsFor} onClose={() => setAdminsFor(null)} />}
        </Wrapper>
    )
}

const selectSx = {
    minWidth: 200,
    borderRadius: ui.radius,
    fontSize: '13.5px',
    fontWeight: 600,
    backgroundColor: ui.card,
    '& .MuiOutlinedInput-notchedOutline': { borderColor: ui.border },
}

export default SchoolsPage
